import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import {
  Box,
  FormControlLabel,
  Radio,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import { marketplaceTypography as mt } from "../constants/marketplaceTypography";
import { marketplaceCardSx } from "../constants/marketplaceCardStyles";
import type { MarketplaceAddressFormValues } from "../types";
import { formatAddressDisplayLines } from "../utils/addressHelpers";

type CheckoutAddressOptionCardProps = {
  value: string;
  selected: boolean;
  heading: string;
  values?: MarketplaceAddressFormValues;
  variant?: "saved" | "new";
};

function AddressLines({ values }: { values: MarketplaceAddressFormValues }) {
  const { streetLine, cityLine, cepLine } = formatAddressDisplayLines(values);
  const lines = [streetLine, cityLine, cepLine].filter(Boolean);

  if (lines.length === 0) {
    return (
      <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }}>
        Endereço incompleto
      </Typography>
    );
  }

  return (
    <Stack spacing={0.35}>
      {lines.map((line) => (
        <Typography
          key={line}
          sx={{
            ...mt.bodySecondary,
            color: "text.secondary",
            lineHeight: 1.45,
          }}
        >
          {line}
        </Typography>
      ))}
    </Stack>
  );
}

/** Card selecionável de endereço — padrão address-book checkout. */
export function CheckoutAddressOptionCard({
  value,
  selected,
  heading,
  values,
  variant = "saved",
}: CheckoutAddressOptionCardProps) {
  const theme = useTheme();
  const isNew = variant === "new";

  const Icon = isNew
    ? AddLocationAltOutlinedIcon
    : heading.toLowerCase().includes("perfil")
      ? HomeOutlinedIcon
      : LocalShippingOutlinedIcon;

  return (
    <Box
      sx={{
        ...marketplaceCardSx(theme),
        p: 0,
        borderColor: selected ? "primary800.main" : alpha(theme.palette.divider, 0.9),
        bgcolor: selected
          ? alpha(theme.palette.primary800.main, 0.05)
          : "background.paper",
        boxShadow: selected
          ? `0 0 0 1px ${alpha(theme.palette.primary800.main, 0.35)}`
          : marketplaceCardSx(theme).boxShadow,
      }}
    >
      <FormControlLabel
        value={value}
        control={<Radio sx={{ p: 1.25, ml: 0.5 }} />}
        label={
          <Stack direction="row" spacing={1.5} alignItems="flex-start" py={1.5} pr={2}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                bgcolor: selected
                  ? alpha(theme.palette.primary800.main, 0.12)
                  : alpha(theme.palette.primary800.main, 0.06),
                color: "primary800.main",
              }}
            >
              <Icon fontSize="small" />
            </Box>
            <Stack spacing={0.75} minWidth={0} flex={1}>
              <Typography sx={{ ...mt.label, color: "text.primary" }}>
                {heading}
              </Typography>
              {values ? <AddressLines values={values} /> : null}
              {isNew ? (
                <Typography sx={{ ...mt.bodySecondary, color: "text.secondary" }}>
                  Preencher um endereço diferente para esta entrega
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        }
        sx={{
          m: 0,
          width: "100%",
          alignItems: "flex-start",
          "& .MuiFormControlLabel-label": { width: "100%" },
        }}
      />
    </Box>
  );
}
